import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Eye, EyeOff, ArrowLeft } from "lucide-react";

const T = {
  tr: {
    title: "Yeni şifre belirle",
    subtitle: "Aşağıya yeni şifreni gir. En az 6 karakter olmalı.",
    newPwd: "Yeni şifre",
    confirmPwd: "Yeni şifreyi tekrar gir",
    submit: "Şifreyi değiştir",
    submitting: "Kaydediliyor…",
    backLogin: "Girişe dön",
    mismatch: "Şifreler eşleşmiyor.",
    tooShort: "Şifre en az 6 karakter olmalı.",
    noToken: "Geçersiz veya eksik bağlantı.",
    okTitle: "Şifren güncellendi",
    okBody: "Artık yeni şifrenle giriş yapabilirsin.",
    goLogin: "Girişe git",
  },
  de: {
    title: "Neues Passwort setzen",
    subtitle: "Gib unten dein neues Passwort ein. Mindestens 6 Zeichen.",
    newPwd: "Neues Passwort",
    confirmPwd: "Neues Passwort wiederholen",
    submit: "Passwort ändern",
    submitting: "Wird gespeichert…",
    backLogin: "Zurück zur Anmeldung",
    mismatch: "Passwörter stimmen nicht überein.",
    tooShort: "Mindestens 6 Zeichen.",
    noToken: "Ungültiger oder fehlender Link.",
    okTitle: "Passwort aktualisiert",
    okBody: "Du kannst dich jetzt mit deinem neuen Passwort anmelden.",
    goLogin: "Zur Anmeldung",
  },
};

export default function ResetPassword() {
  const { lang } = useI18n();
  const t = T[lang] || T.tr;
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tooShort = pwd.length > 0 && pwd.length < 6;
  const mismatch = pwd2.length > 0 && pwd !== pwd2;
  const canSubmit = !loading && !!token && pwd.length >= 6 && pwd === pwd2;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token, new_password: pwd, password_confirm: pwd2,
      });
      setDone(true);
      toast.success(t.okTitle);
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="reset-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-lg">R</div>
          <span className="font-display font-bold text-lg">RestroPilot</span>
        </div>

        <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-4 mb-6 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
            <KeyRound className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-sm tracking-tight">{t.title}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{t.subtitle}</div>
          </div>
        </div>

        {!token && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900" data-testid="reset-no-token">
            {t.noToken}
          </div>
        )}

        {token && !done && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div>
              <Label>{t.newPwd}</Label>
              <div className="relative mt-1">
                <Input
                  type={showPwd ? "text" : "password"}
                  required minLength={6}
                  autoComplete="new-password"
                  value={pwd} onChange={(e) => setPwd(e.target.value)}
                  data-testid="reset-password"
                  className={`pr-10 ${tooShort ? "border-rose-400" : ""}`}
                />
                <button type="button" onClick={() => setShowPwd(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                  data-testid="reset-password-toggle" aria-label={showPwd ? "hide" : "show"}>
                  {showPwd ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                </button>
              </div>
              {tooShort && <p className="text-xs text-rose-600 mt-1" data-testid="reset-too-short">{t.tooShort}</p>}
            </div>
            <div>
              <Label>{t.confirmPwd}</Label>
              <div className="relative mt-1">
                <Input
                  type={showPwd2 ? "text" : "password"}
                  required minLength={6}
                  autoComplete="new-password"
                  value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                  data-testid="reset-password-confirm"
                  className={`pr-10 ${mismatch ? "border-rose-400" : ""}`}
                  aria-invalid={mismatch}
                />
                <button type="button" onClick={() => setShowPwd2(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                  data-testid="reset-password-confirm-toggle" aria-label={showPwd2 ? "hide" : "show"}>
                  {showPwd2 ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                </button>
              </div>
              {mismatch && <p className="text-xs text-rose-600 mt-1" data-testid="reset-mismatch">{t.mismatch}</p>}
            </div>
            <Button type="submit" disabled={!canSubmit} className="w-full h-11 active:scale-95" data-testid="reset-submit">
              {loading ? t.submitting : t.submit}
            </Button>
          </form>
        )}

        {done && (
          <div className="rounded-2xl border p-6 bg-emerald-50 border-emerald-200 space-y-3" data-testid="reset-done">
            <div className="font-medium text-emerald-900">{t.okTitle}</div>
            <div className="text-xs text-emerald-800">{t.okBody}</div>
            <Link to="/login" className="text-sm text-primary hover:underline" data-testid="reset-go-login">
              {t.goLogin}
            </Link>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="reset-back-login">
            <ArrowLeft className="h-3.5 w-3.5" /> {t.backLogin}
          </Link>
        </div>
      </div>
    </div>
  );
}
